type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** The one 401 that means "wrong password", not "your session ended". */
const LOGIN_PATH = "/api/auth/login";

/**
 * Wrap `fetch` so an expired session drops the app back to the login screen
 * instead of quietly rendering empty pages.
 *
 * Done once, centrally, rather than at each of the ~40 call sites: an
 * endpoint added later is covered because it goes through fetch, not because
 * someone remembered to handle 401 there. The response is still returned
 * unchanged, so no existing caller's error handling shifts underneath it.
 *
 * `origin` is passed in rather than read off `location` so the URL matching
 * is a plain function of its arguments — and so it behaves identically
 * wherever it runs instead of silently matching nothing outside a browser.
 */
export function wrapFetchForAuth({
	baseFetch,
	origin,
	onUnauthorized,
}: {
	baseFetch: FetchFn;
	origin: string;
	onUnauthorized: () => void;
}): FetchFn {
	const pathOf = (input: RequestInfo | URL): string | null => {
		const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
		try {
			const url = new URL(raw, origin);
			// Same origin only: a 401 from OpenRouter or any other third party
			// says nothing about this app's session.
			return url.origin === origin ? url.pathname : null;
		} catch {
			return null;
		}
	};

	return async (input, init) => {
		const res = await baseFetch(input, init);
		if (res.status === 401) {
			const path = pathOf(input);
			if (path?.startsWith("/api/") && path !== LOGIN_PATH) onUnauthorized();
		}
		return res;
	};
}
